import "./App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AuthCallback from "./pages/AuthCallback";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Lives from "./pages/Lives";
import LiveDetail from "./pages/LiveDetail";
import Products from "./pages/Products";
import Customers from "./pages/Customers";
import OrphanHandles from "./pages/OrphanHandles";
import PendingByHandleReport from "./pages/PendingByHandleReport";
import Contractors from "./pages/Contractors";
import ContractorReports from "./pages/ContractorReports";
import Expedition from "./pages/Expedition";
import ExpeditionAdmin from "./pages/ExpeditionAdmin";
import CustomerBoxes from "./pages/CustomerBoxes";
import MyProfile from "./pages/MyProfile";
import MyPendingLives from "./pages/MyPendingLives";
import Departments from "./pages/Departments";
import Loyalty from "./pages/Loyalty";
import LoyaltyDetail from "./pages/LoyaltyDetail";
import UsersAdmin from "./pages/UsersAdmin";
import RolesAdmin from "./pages/RolesAdmin";
import AppShell from "./components/AppShell";
import HandleConfirmGate from "./components/HandleConfirmGate";
import MergeHandles from "./pages/MergeHandles";

function Protected({ children, perm, fallback = "/lives" }) {
  const { user, loading, hasPerm } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center text-brand-ink font-bold uppercase tracking-widest">Carregando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Optional perm gate — redirect to a sensible fallback instead of showing
  // the shell + a 403'd empty page when the user truly can't access this one.
  if (user.needs_handle) return <HandleConfirmGate />;
  if (perm && !hasPerm(perm)) return <Navigate to={fallback} replace />;
  return <AppShell>{children}</AppShell>;
}

function MasterOnly({ children }) {
  const { user } = useAuth();
  if (!user?.is_master) return <Navigate to="/dashboard" replace />;
  return children;
}

function OrphansOnly({ children }) {
  const { user } = useAuth();
  if (!user?.can_view_orphans) return <Navigate to="/customers" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={<Protected perm="dashboard.view" fallback="/lives"><Dashboard /></Protected>} />
      <Route path="/lives" element={<Protected><Lives /></Protected>} />
      <Route path="/lives/:id" element={<Protected><LiveDetail /></Protected>} />
      <Route path="/products" element={<Protected><Products /></Protected>} />
      <Route path="/departments" element={<Protected><Departments /></Protected>} />
      <Route path="/customers" element={<Protected><Customers /></Protected>} />
      <Route path="/customers/orphan-handles" element={<Protected><OrphansOnly><OrphanHandles /></OrphansOnly></Protected>} />
      <Route path="/customers/merge" element={<Protected><MasterOnly><MergeHandles /></MasterOnly></Protected>} />
      <Route path="/reports/pending-by-handle" element={<Protected><PendingByHandleReport /></Protected>} />
      <Route path="/contractors" element={<Protected><Contractors /></Protected>} />
      <Route path="/reports/contractors" element={<Protected><ContractorReports /></Protected>} />
      <Route path="/expedition" element={<Protected><Expedition /></Protected>} />
      <Route path="/expedition/admin" element={<Protected><ExpeditionAdmin /></Protected>} />
      <Route path="/expedition/customer-boxes" element={<Protected><CustomerBoxes /></Protected>} />
      <Route path="/me/profile" element={<Protected><MyProfile /></Protected>} />
      <Route path="/me/pending-lives" element={<Protected><MyPendingLives /></Protected>} />
      <Route path="/loyalty" element={<Protected><Loyalty /></Protected>} />
      <Route path="/loyalty/:handle" element={<Protected><LoyaltyDetail /></Protected>} />
      <Route path="/admin/users" element={<Protected><MasterOnly><UsersAdmin /></MasterOnly></Protected>} />
      <Route path="/admin/roles" element={<Protected><MasterOnly><RolesAdmin /></MasterOnly></Protected>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

const TOAST_OPTIONS = {
  className: "!bg-white !border-2 !border-brand-ink !shadow-brutal !rounded-none !text-brand-ink !font-bold",
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" toastOptions={TOAST_OPTIONS} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
